// Events
// --------------
// You can trigger the following to force a slide to the next group:
// slide-to-next-item


import { throttle } from "lodash";
import { Promise } from "es6-promise";
import {
	itemsPerGroupObjectToHtmlAttr,
	getItemsPerGroupDataFromWrapAttr,
	getWaitValueFromWrapAttr,
	getItemsPerGroup,
	ItemsPerGroupSetting,
} from "./slider-helpers";
import * as classnames from "classnames";

declare const jQuery: any;
const $ = jQuery;

export interface CreateOptions
{
	wait?: number; // secs
	itemsPerGroup?: ItemsPerGroupSetting[];
	className?: string|string[];
}

const config = Object.freeze({
	dfItemsPerGroup: [
		{ minWindowWidth: 0, nItems: 1 },
	]
});

export function initInfiniteUnidirectionalAutoSliders(): void
{
	const $window = $(window);
	$(".infinite-unidirectional-auto-slider").each(function () {
		initSlider($(this), $window);
	});
}

export function createInfiniteUnidirectionalAutoSlider($sliderParent, $items, options?: CreateOptions): void
{
	options = options || {};
	const className: string = classnames("slider", "infinite-unidirectional-auto-slider", options.className);
	const $wrap = $(`<div class="${className}"></div>`);
	if (options.wait)
	{
		$wrap.attr("data-slider-wait", options.wait.toString());
	}
	if (options.itemsPerGroup)
	{
		$wrap.attr("data-slider-items-per-group", itemsPerGroupObjectToHtmlAttr(options.itemsPerGroup));
	}
	const $inner = $(`<div>`).appendTo($wrap);
	const $display = $(`<div class="slider-items-display">`).appendTo($inner);
	const $slider = $(`<div class="slider-items-slider">`).appendTo($display);
	$items.each(function () {
		const $itemWrap = $(`<div class="slider-item">`).append(this);
		$slider.append($itemWrap);
	});
	$sliderParent.append($wrap);
	initSlider($wrap, $(window));
}

function initSlider($wrap, $window): void
{
	let wait: number = $wrap.hasClass("constant") ? 1152 : getWaitValueFromWrapAttr($wrap, false);
	const $display = $wrap.find(".slider-items-display");
	const $slider = $wrap.find(".slider-items-slider");
    const itemsPerGroupSettings = getItemsPerGroupDataFromWrapAttr($wrap, config.dfItemsPerGroup);
    let transitionDuration: string = "";

    if ($wrap.hasClass("constant")) {
        const constantFactor: number = parseFloat($wrap.attr("data-constant-factor"));
        if (constantFactor && constantFactor >= 0) {
            wait *= constantFactor;
            transitionDuration = `${1200 * constantFactor}ms`;
            $slider.find(".slider-item").css("transition-duration", transitionDuration);
        }
    }

	let timer: any;
	let itemIndex: number = 0;

	function next(): void
	{
		const $items = $slider.children();
		const numItems: number = $items.length;
		const itemsToModify = getItemsToModify($items, itemIndex, getItemsPerGroup($window, itemsPerGroupSettings));
		if (!itemsToModify)
		{
			return;
		}
		++itemIndex;
		if (itemIndex === numItems)
		{
			itemIndex = 0;
		}
		const $itemsToSlide = itemsToModify.$itemsToSlide;
		const $queuedItem = itemsToModify.$queuedItem;
		const queuedStartPosition: number = $display.outerWidth() - $queuedItem[0]._origPositionX;
		immediateTranslateX($queuedItem, queuedStartPosition).then(function () {
			$queuedItem[0]._prevTranslateX = queuedStartPosition;
			const itemWidth: number = $items.outerWidth();
			$itemsToSlide.each(function () {
				const $item = $(this);
				const item = $item[0];
				const x: number = item._prevTranslateX = item._prevTranslateX - itemWidth;
                $item.css({ transform: `translateX(${x}px)` });
                if (transitionDuration) {
                    $item.css({ transitionDuration });
                }
			});
			waitThenNext();
		});
	}
	
	function waitThenNext(): void
	{
		clearTimeout(timer);
		if (wait && wait > 0)
		{
			timer = setTimeout(next, wait);
		}
	}

	resetLayout($slider, $window, itemsPerGroupSettings);

    let prevWindowWidth: number = $window.outerWidth();

    // fixme - track curr item index & reset to this value (not 0)
    // when load|resize fired below:
    
    setOrigPositions($slider).then(function () {
        $window.on("load resize", throttle(() => {
            const newWindowWidth: number = $window.outerWidth();
            if (newWindowWidth === prevWindowWidth)
            {
                // account for ios treating scroll as resize
                return;
            }
            prevWindowWidth = newWindowWidth;
            clearTimeout(timer);
			itemIndex = 0;
			resetLayout($slider, $window, itemsPerGroupSettings);
			setOrigPositions($slider).then(waitThenNext);
		}, 50));
		$wrap.addClass("show");
		waitThenNext();
	});

	$wrap.on("slide-to-next-item", () => {
		clearTimeout(timer);
		next();
	});
}

function setOrigPositions($slider): Promise<any>
{
	const $items = $slider.children();
	return immediateTranslateXMultiple($items, 0).then(function () {
		$items.each(function () {
			const item = this;
			const $item = $(item);
			item._origPositionX = $item.position().left;
			item._prevTranslateX = 0;
		});
	});
}

function setTimeoutPromise(fn: () => any, ms: number): Promise<any>
{
	return new Promise<any>((resolve, reject): void => {
		setTimeout(() => {
			resolve(fn());
		}, ms);
	});
}

function immediateTranslateXMultiple($items, x: number): Promise<void[]>
{
	const promises: Promise<void>[] = [];
	$items.each(function () {
		promises.push(immediateTranslateX($(this), 0));
	});
	return Promise.all(promises);
}

function immediateTranslateX($item, x: number): Promise<void>
{
	$item.css({ transition: "none" });
	return new Promise<void>((resolve, reject) => {
		setTimeout(() => {
			$item.css({ transform: `translateX(${x}px)` });
			setTimeout(() => {
				$item.css({ transition: "" });
				resolve();
			}, 25);
		}, 25);
	});
}

function getItemsToModify($allItems, itemIndex: number, itemsPerGroup: number)
{
	const numItems: number = $allItems.length;
	if (numItems <= itemsPerGroup)
	{
		return undefined;
	}
	const numToSlide: number = itemsPerGroup + 1;
	let i: number = itemIndex;
	let $itemsToSlide = $();
	for (let count: number = 0; count < numToSlide; ++count)
	{
		$itemsToSlide = $itemsToSlide.add($allItems.eq(i));
		if (count < itemsPerGroup)
		{
			++i;
			if (i === numItems)
			{
				i = 0;
			}
		}
	}
	return {
		$itemsToSlide,
		$queuedItem: $allItems.eq(i)
	};
}

function resetLayout($slider, $window, itemsPerGroupSettings): void
{
	const itemsPerGroup: number = getItemsPerGroup($window, itemsPerGroupSettings);
	const $items = $slider.children();
	const numItems: number = $items.length;
	const sliderWidthPercent: number = (100 / itemsPerGroup) * numItems;
	$slider.css({ width: sliderWidthPercent + "%" });
	const itemWidthPercent: number = 100 / numItems;
	$items.css({ width: itemWidthPercent + "%" });
}

if (!window["iw"])
{
	window["iw"] = {};
}
if (!window["iw"].sliders)
{
	window["iw"].sliders = {};
}
window["iw"].sliders.createInfiniteUnidirectionalAutoSlider = createInfiniteUnidirectionalAutoSlider;
